import { API_GBP_CURRENCY_CODE } from '@constants/apiConstants';
import { CURRENCY_CODE } from '@constants/app';
import { nonDigitRegex } from '@constants/regEx';
import {
  AmountCredit,
  Charge,
  ChargeAmount,
  ChargeType,
  Discount,
  DiscountCredit,
  PercentageCredit,
  Quote,
} from '@generated/gql/graphql';
import { formatCurrency } from '@utils/format';

const parseCurrency = (price: ChargeAmount) => ({
  ...price,
  currency:
    price.currency === API_GBP_CURRENCY_CODE ? CURRENCY_CODE : price.currency,
});

const sumPrices = (price: ChargeAmount, otherPrice: ChargeAmount) => ({
  ...price,
  currency:
    price.currency === API_GBP_CURRENCY_CODE ? CURRENCY_CODE : price.currency,
  value: (Number(price.value) + Number(otherPrice.value)).toString(),
});

const subtractPrices = (price: ChargeAmount, otherPrice: ChargeAmount) => ({
  ...price,
  currency:
    price.currency === API_GBP_CURRENCY_CODE ? CURRENCY_CODE : price.currency,
  value: (Number(price.value) - Number(otherPrice.value)).toString(),
});

const parseValueToPennies = (value: string) =>
  Math.round(parseFloat(value) * 100);

const convertValueToCurrency = (value: number): ChargeAmount => {
  if (isNaN(value)) {
    throw new Error(`convertValueToCurrency: Invalid value of ${value}`);
  }

  return {
    currency: CURRENCY_CODE,
    value: value.toFixed(2),
  };
};

const isAmountCredit = (credit: DiscountCredit): credit is AmountCredit =>
  credit.__typename === 'AmountCredit';

const isPercentageCredit = (
  credit: DiscountCredit
): credit is PercentageCredit => credit.__typename === 'PercentageCredit';

const calculateDiscountedPrice = (
  price: ChargeAmount,
  discount: Pick<Discount, 'qualified' | 'credit'>,
  qualifiedOnly: boolean = true
): ChargeAmount => {
  const { qualified, credit } = discount;

  if (qualifiedOnly && !qualified) {
    return price;
  }

  if (isAmountCredit(credit)) {
    return {
      ...price,
      value: (Number(price.value) - Number(credit.amount)).toString(),
    };
  }

  if (isPercentageCredit(credit)) {
    return {
      ...price,
      value: (Number(price.value) * (1 - credit.percentage / 100)).toString(),
    };
  }

  return price;
};

const trimPriceDecimals = (price: string) => {
  price = price.replace(/,/g, '');
  const isNegative = price.startsWith('-');
  const value = parseFloat(price.replace(nonDigitRegex, ''));

  if (isNaN(value)) {
    return 'Invalid price';
  }

  const roundedValue = Math.abs(Math.round(value));
  const currencySymbol = price.match(nonDigitRegex)?.[0] ?? '';

  return isNegative
    ? `-${currencySymbol}${roundedValue}`
    : `${currencySymbol}${roundedValue}`;
};

const getMonthlyTotalAfterCashback = (
  monthlyCost: ChargeAmount | undefined,
  cashbackValue: Nullable<string>
): string => {
  if (!cashbackValue || !monthlyCost) {
    return formatCurrency({ value: monthlyCost?.value ?? '' });
  }

  const cbcSavingPrice = convertValueToCurrency(Number(cashbackValue));

  return formatCurrency(subtractPrices(monthlyCost, cbcSavingPrice));
};

const getMonthlyCost = (monthlyCost: ChargeAmount | null): ChargeAmount => {
  if (monthlyCost) {
    return {
      ...monthlyCost,
    };
  }
  return {
    value: '0',
    currency: CURRENCY_CODE,
  };
};

const getQuoteMonthlyCharge = (quote: Quote) => {
  return quote.product.charges.find(
    (charge) => charge.type === ChargeType.MonthlyRecurring
  );
};

const getQuoteMonthlyPrice = (quote: Quote) => {
  const monthlyCharge = getQuoteMonthlyCharge(quote);

  if (!monthlyCharge) {
    return formatCurrency({ value: '0' });
  }

  return formatCurrency(monthlyCharge.amount);
};

const calculateTotalWithDiscounts = (
  totalMonthlyCost: ChargeAmount,
  charge: Charge,
  discounts: Pick<Discount, 'qualified' | 'credit'>[]
): ChargeAmount => {
  const discountedCharge = discounts.reduce((acc, discount) => {
    return calculateDiscountedPrice(acc, discount);
  }, charge.amount);

  const discount = Number(charge.amount.value) - Number(discountedCharge.value);

  const totalValue = Number(totalMonthlyCost.value) - discount;

  return {
    ...totalMonthlyCost,
    value: totalValue.toFixed(2),
  };
};

export {
  convertValueToCurrency,
  sumPrices,
  subtractPrices,
  parseCurrency,
  parseValueToPennies,
  isAmountCredit,
  calculateDiscountedPrice,
  trimPriceDecimals,
  getMonthlyCost,
  getMonthlyTotalAfterCashback,
  getQuoteMonthlyCharge,
  getQuoteMonthlyPrice,
  calculateTotalWithDiscounts,
};
